import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SquarePen } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import LogoutConfirmDialog from '@/components/common/LogoutConfirmDialog.tsx';

import PaginationComponent from '@/components/common/PaginationComponent';
import { useToast } from '@/hooks/use-toast';
import CSSearchForm from '@/components/cs/CSSearchForm.tsx';
import { useGraphQLSubscription } from '@/hooks/useGraphQLSubscription';
import { visiblePatientsService, MappedVisiblePatient } from '@/services/features/VisiblePatientsService';

const CSUserDashboard = () => {
  const { user, logout, token } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [patients, setPatients] = useState<MappedVisiblePatient[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalPages, setTotalPages] = useState(0);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  
  // Search state
  const [searchBy, setSearchBy] = useState('name');
  const [searchTerm, setSearchTerm] = useState('');

  // Add GraphQL subscription for real-time transfer notifications
  const { notificationCount } = useGraphQLSubscription();

  const handleSearchPatientsClick = () => {
    navigate('/global-search');
  };

  // Load patients using visiblePatients REST API
  useEffect(() => {
    if (token && user?.id) {
      loadPatients();
    }
  }, [token, user?.id, currentPage, rowsPerPage]);

  const handleRecordsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  const loadPatients = async () => {
    if (!token || !user?.id) {
      console.error('No token or user ID available');
      return;
    }

    setIsLoading(true);
    try {
      // API uses 0-based page index
      const apiPage = currentPage - 1;
      
      const response = await visiblePatientsService.getVisiblePatients(
        Number(user.id),
        apiPage,
        rowsPerPage,
        token || undefined
      );

      setPatients(response.patients);
      setTotalCount(response.totalItems);
      setTotalPages(response.totalPages);
    } catch (error) {
      console.error('Error loading patients:', error);
      toast({
        title: "Error",
        description: "Failed to load patient data",
        variant: "destructive",
      });
      setPatients([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
    toast({
      title: "Success",
      description: "You have been logged out successfully",
    });
  };

  return (
    <div className="min-h-screen bg-white" id="cs-user-dashboard">

      {/* Search and Filter Bar with Navigation Buttons */}
      <div className="bg-white border-b py-4">
        <div className="flex items-center justify-between space-x-4">
          <CSSearchForm
            searchBy={searchBy}
            searchTerm={searchTerm}
            onSearchByChange={setSearchBy}
            onSearchTermChange={setSearchTerm}
            onSearch={loadPatients}
            onReload={() => {
              setSearchTerm('');
              setSearchBy('name');
              loadPatients();
            }}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Main Content */}
      <main className="p-6 px-0">

        <Card className="bg-white shadow-sm">
          <CardContent className="p-6">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : patients.length === 0 ? (
              <div className="p-12 text-center">
                <h3 className="text-xl font-medium text-gray-900 mb-4">
                  Congrats! You've cleared all the assigned patients for today.
                </h3>
              </div>
            ) : (
              <>
                {/* Table */}
                <div className="rounded-lg border border-border overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-muted/50 border-b border-border">
                        <th className="font-bold text-foreground h-12 px-4 text-left">Patient Name</th>
                        <th className="font-bold text-foreground h-12 px-4 text-left">DOB</th>
                        <th className="font-bold text-foreground h-12 px-4 text-left">EMR ID</th>
                        <th className="font-bold text-foreground h-12 px-4 text-left">Clinic Name</th>
                        <th className="font-bold text-foreground h-12 px-4 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {patients.map((patient) => (
                        <tr
                          key={patient.patient_program_id}
                          className="border-b border-border hover:bg-muted/30 transition-colors"
                        >
                          <td className="py-4 px-4">
                            <span className="font-medium text-foreground">
                              {patient.patient_full_name || 'N/A'}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-muted-foreground text-sm">
                              {patient.dob || 'N/A'}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold border border-primary/20">
                              {patient.emr_id || 'N/A'}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-muted-foreground text-sm">
                              {patient.clinic_name || 'N/A'}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/es-dashboard?from=cs-dashboard&patientId=${patient.patient_id}&activityId=${patient.activity_id || ''}&workflowId=${patient.workflow_id || ''}`)}
                              className="hover:bg-primary hover:text-primary-foreground transition-colors"
                            >
                              <SquarePen className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {!isLoading && totalCount > 0 && (
          <div className="mt-6">
            <PaginationComponent
              currentPage={currentPage}
              totalPages={totalPages}
              totalRecords={totalCount}
              startIndex={(currentPage - 1) * rowsPerPage}
              endIndex={Math.min(currentPage * rowsPerPage, totalCount)}
              onPageChange={setCurrentPage}
              recordsPerPage={rowsPerPage}
              onRecordsPerPageChange={handleRecordsPerPageChange}
            />
          </div>
        )}
      </main>

      <LogoutConfirmDialog
        open={showLogoutDialog}
        onOpenChange={setShowLogoutDialog}
        onConfirm={handleLogout}
      />
    </div>
  );
};

export default CSUserDashboard;
